import { format } from 'date-fns'
import { Updatable } from '../../core/models/updatable'
import { Version } from '../../core/models/version'
import { Engineer } from '../../organisation/models/engineer'
import { alias, AliasedConstructor } from '../../rest/decorators/alias'
import {
  DynamicBinding,
  SerialisationConfig,
} from '../../rest/utilities/serialisable'
import { Job } from './job'
import { JobStatus } from './job-status'

export class BaseJobAllocation extends Updatable {
  public static override serialisationConfig: SerialisationConfig =
    new SerialisationConfig(() => new BaseJobAllocation(), {
      job: new SerialisationConfig(() => new Job()),
      engineer: Engineer.serialisationConfig,
      internalStatus: JobStatus.serialisationConfig,
      externalStatus: JobStatus.serialisationConfig,
      firstVersion: new SerialisationConfig(() => new Version()),
      lastVersion: new SerialisationConfig(() => new Version()),
      currentVersion: new SerialisationConfig(() => new Version()),
    })
  override mandatory = ['id']

  @alias('id')
  id!: number
  @alias('numeric_id')
  numericID!: number
  @alias('job')
  job!: Job | null
  @alias('engineer')
  engineer!: Engineer | null
  @alias('internal_status')
  internalStatus!: JobStatus | null
  @alias('external_status')
  externalStatus!: JobStatus | null
  @alias('duplicated_allocation_id')
  duplicateID!: number
  @alias('hq_workshop')
  hqWorkshop!: boolean
  @alias('midlands_workshop')
  midlandsWorkshop!: boolean
  @alias('parts_required')
  partsRequired!: boolean
  @alias('parts_ordered')
  partsOrdered!: boolean
  @alias('parts_fulfilled')
  partsFulfilled!: boolean
  @alias('is_lone_worker')
  loneWorker!: boolean
  @alias('is_provisional')
  provisional!: boolean
  @alias('date')
  date!: Date
  @alias('start_at')
  start!: Date
  @alias('end_at')
  end!: Date
  @alias('additional_information')
  additionalInformation!: string
  @alias('registration')
  vehicleRegistration!: string
  @alias('chassis_number')
  chassisNumber!: string
  @alias('mileage')
  mileage!: number | null
  @alias('pending_post_work_images')
  pendingPostWorkImages?: number | undefined
  @alias('sent_at')
  sent!: Date | null
  @alias('received_at')
  received!: Date | null
  @alias('seen_at')
  seen!: Date | null
  @alias('on_site_at')
  onSite!: Date | null
  @alias('off_site_at')
  offSite!: Date | null
  @alias('completed_at')
  completed!: Date | null
  @alias('approved_at')
  approved!: Date | null
  @alias('invoiced_at')
  invoiced!: Date | null
  @alias('excepted_at')
  excepted!: Date | null
  @alias('is_last_allocation')
  lastAllocation!: boolean
  @alias('deleted')
  deleted!: boolean
  @alias('first_version')
  firstVersion!: Version | null
  @alias('last_version')
  lastVersion!: Version | null
  @alias('current_version')
  currentVersion!: Version | null
  constructor(aliases?: unknown) {
    super(
      aliases == null
        ? (BaseJobAllocation as unknown as AliasedConstructor)._alias
        : Object.assign(
            {},
            (BaseJobAllocation as unknown as AliasedConstructor)._alias,
            aliases
          )
    )
  }

  get time(): string {
    return this.start && this.end
      ? `${format(this.start, 'HH:mm')} - ${format(this.end, 'HH:mm')}`
      : ''
  }

  get fullDate(): string {
    const components = []
    if (this.date) {
      components.push(format(this.date, 'dd/MM/yyyy'))
    }
    if (this.start && this.end) {
      components.push(this.time)
    }
    return components.join('\n')
  }

  get workshopName(): string {
    if (this.hqWorkshop) {
      return 'HQ'
    } else if (this.midlandsWorkshop) {
      return 'Midlands'
    } else {
      return '-'
    }
  }

  get title(): string {
    const components = []
    if (this.job && this.job.internalID) {
      components.push(this.job.internalID)
    }
    if (this.start && this.end) {
      components.push(
        `${format(this.start, 'HH:mm')} - ${format(this.end, 'HH:mm')}`
      )
    }
    if (this.job) {
      if (this.job.postcode) {
        components.push(this.job.postcode)
      }
      if (this.job.jobNumber) {
        components.push(this.job.jobNumber)
      }
      if (this.job.customer && this.job.customer.name) {
        components.push(this.job.customer.name)
      }
    }
    return components.join(' | ')
  }

  override serialise(): object {
    const data = super.serialise() as DynamicBinding
    if (Object.prototype.hasOwnProperty.call(data, 'job')) {
      data['job_id'] = this.job?.id ?? null
      delete data['job']
    }
    if (Object.prototype.hasOwnProperty.call(data, 'engineer')) {
      data['engineer_id'] = this.engineer?.id ?? null
      delete data['engineer']
    }
    if (data['date']) {
      data['date'] = format(this.date, 'yyyy-MM-dd')
    }
    if (data['start_at']) {
      data['start_at'] = format(this.start, 'HH:mm')
    }
    if (data['end_at']) {
      data['end_at'] = format(this.end, 'HH:mm')
    }
    if (data['is_provisional']) {
      data['is_provisional'] = data['is_provisional'] ?? false
    }
    if (data['hq_workshop']) {
      data['hq_workshop'] = data['hq_workshop'] ?? false
    }
    if (data['midlands_workshop']) {
      data['midlands_workshop'] = data['midlands_workshop'] ?? false
    }
    if (
      Object.prototype.hasOwnProperty.call(data, 'mileage') &&
      data['mileage'] === 0
    ) {
      data['mileage'] = null
    }
    if (data['internal_status']) {
      delete data['internal_status']
    }
    if (data['external_status']) {
      delete data['external_status']
    }
    if (data['first_version']) {
      delete data['first_version']
    }
    if (data['last_version']) {
      delete data['last_version']
    }
    if (data['current_version']) {
      delete data['current_version']
    }
    if (data['is_last_allocation']) {
      delete data['is_last_allocation']
    }
    return data
  }
}
